import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2, Pencil, Plus } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { DataTable, type RowAction } from "@/components/data-table/DataTable";
import { usePagedQuery } from "@/components/data-table/usePagedQuery";
import { getTipoArquivo, saveTipoArquivo, TIPO_ARQUIVO_PATH, type TipoArquivo, type TipoArquivoFiltro } from "./api";
import { tipoArquivoSchema, type TipoArquivoFormValues } from "./schema";

// Porte de app/controllers/consulta-tipo-arquivo.controller.js +
// app/templates/tipo-arquivo/consulta-tipo-arquivo.html — primeira rota real
// migrada (Fase 1 do plano). Excluir fica de fora de propósito: o botão já vem
// comentado no template legado, nunca esteve ativo.
const QUERY_KEY = ["tipo-arquivo"];

const columns: ColumnDef<TipoArquivo, unknown>[] = [
  { accessorKey: "id", header: "ID", enableSorting: true },
  { accessorKey: "descricao", header: "Descrição", enableSorting: true },
];

export default function ConsultaTipoArquivo() {
  const qc = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  // Registro como veio do GET; o zod descarta chaves fora do schema, então o
  // salvamento sobrepõe os valores do form a ele (ver D14).
  const [registroOriginal, setRegistroOriginal] = useState<TipoArquivo | null>(null);

  const query = usePagedQuery<TipoArquivo, TipoArquivoFiltro>({
    key: QUERY_KEY,
    path: TIPO_ARQUIVO_PATH,
    initialFiltro: {},
  });

  const form = useForm<TipoArquivoFormValues>({
    resolver: zodResolver(tipoArquivoSchema),
    defaultValues: { descricao: "" },
  });

  const save = useMutation({
    mutationFn: saveTipoArquivo,
    onSuccess: () => {
      toast.success("Tipo de arquivo salvo.");
      qc.invalidateQueries({ queryKey: QUERY_KEY });
      setDialogOpen(false);
    },
  });

  function openCreate() {
    form.reset({ id: undefined, descricao: "" });
    setRegistroOriginal(null);
    setDialogOpen(true);
  }

  async function openEdit(row: TipoArquivo) {
    const registro = await getTipoArquivo(row.id);
    form.reset(registro);
    setRegistroOriginal(registro);
    setDialogOpen(true);
  }

  const actions: RowAction<TipoArquivo>[] = [{ label: "Editar", icon: Pencil, onClick: openEdit }];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold text-foreground">Consulta de Tipo de Arquivos</h1>
        <Button onClick={openCreate}>
          <Plus className="mr-2 h-4 w-4" /> Adicionar Novo
        </Button>
      </div>

      <DataTable
        query={query}
        columns={columns}
        actions={actions}
        getRowId={(r) => r.id}
        exportFilename="tipos-de-arquivo"
        emptyMessage="Nenhum tipo de arquivo encontrado."
        toolbar={
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-64">
              <Label htmlFor="f-descricao">Descrição</Label>
              <Input
                id="f-descricao"
                value={query.draft.descricao ?? ""}
                onChange={(e) => query.setDraft((f) => ({ ...f, descricao: e.target.value }))}
                onKeyDown={(e) => e.key === "Enter" && query.search()}
              />
            </div>
            <Button onClick={query.search}>Buscar</Button>
          </div>
        }
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Tipo de Arquivo</DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit((values) => save.mutate({ ...registroOriginal, ...values }))} className="space-y-4">
              <FormField
                control={form.control}
                name="descricao"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Descrição</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={save.isPending}>
                  {save.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {save.isPending ? "Salvando..." : "Salvar"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
